/**
 * BHERMES Metaverse — 3D Hotel Room Viewer (Phase 1 MVP)
 * Direct Three.js rendering — bypasses R3F reconciler for CRA + React 19 compatibility
 * Route: /metaverse/:hotelId
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { buildRoomFromAIConfig } from '@/lib/buildRoomFromAI';
import { createBitsyAvatar } from '@/components/metaverse/BitsyAvatar';
import { VoiceConfigPanel } from '@/components/VoiceConfigPanel';
import VirtualCheckin from '@/components/metaverse/VirtualCheckin';
import MetaverseChat from '@/components/metaverse/MetaverseChat';
import {
  ArrowLeft, Bed as BedIcon, Bath, Maximize, Star, ExternalLink,
  RotateCcw, Loader2, Sparkles, DoorOpen, Wifi, Wind, Tv, Coffee,
  Share2, Copy, Check, Users, MessageCircle, KeyRound, Volume2, VolumeX,
} from 'lucide-react';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const ROOM_TYPES = [
  {
    id: 'standard', name: 'Standard Room',
    size: { w: 6, h: 3.2, d: 5 }, price: 150, maxGuests: 2,
    bedType: 'Queen', sqft: 320, bedW: 1.8,
    amenities: ['WiFi', 'AC', 'TV', 'Mini Bar'],
    colors: { wall: '#e8e0d4', floor: '#8B7355', accent: '#2B6CB0', ceiling: '#f5f0e8' },
  },
  {
    id: 'deluxe', name: 'Deluxe Room',
    size: { w: 7.5, h: 3.4, d: 6 }, price: 280, maxGuests: 2,
    bedType: 'King', sqft: 480, bedW: 2.2,
    amenities: ['WiFi', 'AC', 'TV', 'Mini Bar', 'Balcony', 'Bathrobe'],
    colors: { wall: '#ddd5c8', floor: '#6B5B45', accent: '#B08D57', ceiling: '#f0ebe3' },
  },
  {
    id: 'suite', name: 'Presidential Suite',
    size: { w: 10, h: 3.6, d: 8 }, price: 650, maxGuests: 4,
    bedType: 'King + Sofa', sqft: 860, bedW: 2.2,
    amenities: ['WiFi', 'AC', 'TV', 'Mini Bar', 'Balcony', 'Bathrobe', 'Jacuzzi', 'Living Room'],
    colors: { wall: '#d4cec4', floor: '#4A3F35', accent: '#F7931A', ceiling: '#ece7df' },
  },
];

// ═══════════════════════════════════════════════════════
// THREE.JS SCENE BUILDER
// ═══════════════════════════════════════════════════════

function buildRoom(scene, config) {
  // Clear previous
  while (scene.children.length > 0) scene.remove(scene.children[0]);

  const { w, h, d } = config.size;
  const { wall, floor, ceiling, accent } = config.colors;

  // Lighting
  const ambient = new THREE.AmbientLight(0xffffff, 0.45);
  scene.add(ambient);
  const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
  dirLight.position.set(5, 8, 3);
  dirLight.castShadow = true;
  scene.add(dirLight);

  // Floor
  const floorGeo = new THREE.PlaneGeometry(w, d);
  const floorMat = new THREE.MeshStandardMaterial({ color: floor, roughness: 0.6 });
  const floorMesh = new THREE.Mesh(floorGeo, floorMat);
  floorMesh.rotation.x = -Math.PI / 2;
  floorMesh.receiveShadow = true;
  scene.add(floorMesh);

  // Ceiling
  const ceilMesh = new THREE.Mesh(new THREE.PlaneGeometry(w, d), new THREE.MeshStandardMaterial({ color: ceiling, roughness: 0.9 }));
  ceilMesh.rotation.x = Math.PI / 2;
  ceilMesh.position.y = h;
  scene.add(ceilMesh);

  // Back wall
  const backWall = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshStandardMaterial({ color: wall, roughness: 0.8 }));
  backWall.position.set(0, h / 2, -d / 2);
  scene.add(backWall);

  // Left wall
  const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(d, h), new THREE.MeshStandardMaterial({ color: wall, roughness: 0.8 }));
  leftWall.position.set(-w / 2, h / 2, 0);
  leftWall.rotation.y = Math.PI / 2;
  scene.add(leftWall);

  // Right wall
  const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(d, h), new THREE.MeshStandardMaterial({ color: wall, roughness: 0.8 }));
  rightWall.position.set(w / 2, h / 2, 0);
  rightWall.rotation.y = -Math.PI / 2;
  scene.add(rightWall);

  // Accent wall strip (behind bed)
  const accentStrip = new THREE.Mesh(new THREE.PlaneGeometry(w * 0.6, h * 0.4), new THREE.MeshStandardMaterial({ color: accent, roughness: 0.5, transparent: true, opacity: 0.18 }));
  accentStrip.position.set(0, h * 0.55, -d / 2 + 0.01);
  scene.add(accentStrip);

  // Window (on right wall)
  const winGroup = new THREE.Group();
  winGroup.position.set(w / 2 - 0.01, h * 0.55, -d * 0.15);
  winGroup.rotation.y = -Math.PI / 2;
  const winGlass = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 1.8), new THREE.MeshStandardMaterial({ color: 0x87ceeb, emissive: 0x87ceeb, emissiveIntensity: 0.3, roughness: 0.1 }));
  winGroup.add(winGlass);
  const frameMat = new THREE.MeshStandardMaterial({ color: 0x8b7355 });
  const frameH = new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.06, 0.03), frameMat);
  frameH.position.set(0, 0.9, 0.01);
  winGroup.add(frameH);
  const frameH2 = frameH.clone();
  frameH2.position.y = -0.9;
  winGroup.add(frameH2);
  const frameV = new THREE.Mesh(new THREE.BoxGeometry(0.06, 1.8, 0.03), frameMat);
  frameV.position.set(-1.1, 0, 0.01);
  winGroup.add(frameV);
  const frameV2 = frameV.clone();
  frameV2.position.x = 1.1;
  winGroup.add(frameV2);
  const frameCenter = new THREE.Mesh(new THREE.BoxGeometry(0.04, 1.8, 0.03), frameMat);
  frameCenter.position.set(0, 0, 0.01);
  winGroup.add(frameCenter);
  const winLight = new THREE.PointLight(0xfff5e1, 0.8, 6);
  winLight.position.set(0, 0, 0.5);
  winGroup.add(winLight);
  scene.add(winGroup);

  // ─── Bed ───
  const bw = config.bedW;
  const bedGroup = new THREE.Group();
  bedGroup.position.set(0, 0, -d / 2 + 1.5);
  const darkWood = new THREE.MeshStandardMaterial({ color: 0x3d2b1f, roughness: 0.7 });
  const darkerWood = new THREE.MeshStandardMaterial({ color: 0x2d1f14, roughness: 0.6 });

  // Bed frame
  const frame = new THREE.Mesh(new THREE.BoxGeometry(bw, 0.5, 2.4), darkWood);
  frame.position.y = 0.25;
  frame.castShadow = true;
  bedGroup.add(frame);

  // Mattress
  const mattress = new THREE.Mesh(new THREE.BoxGeometry(bw - 0.1, 0.25, 2.3), new THREE.MeshStandardMaterial({ color: 0xf5f5f0, roughness: 0.9 }));
  mattress.position.y = 0.62;
  bedGroup.add(mattress);

  // Duvet
  const duvet = new THREE.Mesh(new THREE.BoxGeometry(bw - 0.15, 0.08, 1.4), new THREE.MeshStandardMaterial({ color: accent, roughness: 0.8, transparent: true, opacity: 0.9 }));
  duvet.position.set(0, 0.78, 0.15);
  bedGroup.add(duvet);

  // Pillows
  const pillowMat = new THREE.MeshStandardMaterial({ color: 0xfffff0, roughness: 0.95 });
  [-0.4, 0.4].forEach(x => {
    const p = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.12, 0.4), pillowMat);
    p.position.set(x, 0.82, -0.8);
    bedGroup.add(p);
  });

  // Headboard
  const headboard = new THREE.Mesh(new THREE.BoxGeometry(bw + 0.2, 1.2, 0.08), darkerWood);
  headboard.position.set(0, 1.1, -1.16);
  bedGroup.add(headboard);

  // Nightstands + lamps
  [-bw / 2 - 0.45, bw / 2 + 0.45].forEach(x => {
    const ns = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.55, 0.45), darkWood);
    ns.position.set(x, 0.275, -0.9);
    ns.castShadow = true;
    bedGroup.add(ns);
    const lampBase = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.3, 0.12), new THREE.MeshStandardMaterial({ color: accent, roughness: 0.4, metalness: 0.3 }));
    lampBase.position.set(x, 0.7, -0.9);
    bedGroup.add(lampBase);
    const lampShade = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.04, 0.28), new THREE.MeshStandardMaterial({ color: 0xfff8e7, roughness: 0.9 }));
    lampShade.position.set(x, 0.9, -0.9);
    bedGroup.add(lampShade);
    const lampLight = new THREE.PointLight(0xffe4b5, 0.3, 2);
    lampLight.position.set(x, 0.85, -0.9);
    bedGroup.add(lampLight);
  });
  scene.add(bedGroup);

  // ─── Desk ───
  const deskGroup = new THREE.Group();
  deskGroup.position.set(w / 2 - 0.8, 0, d / 2 - 1);
  const deskTop = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.04, 0.6), new THREE.MeshStandardMaterial({ color: 0x5c4033, roughness: 0.5 }));
  deskTop.position.y = 0.75;
  deskTop.castShadow = true;
  deskGroup.add(deskTop);
  [[-0.55, -0.25], [0.55, -0.25], [-0.55, 0.25], [0.55, 0.25]].forEach(([x, z]) => {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.75, 0.04), darkWood);
    leg.position.set(x, 0.375, z);
    deskGroup.add(leg);
  });
  // Chair
  const chairSeat = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.04, 0.45), darkerWood);
  chairSeat.position.set(0, 0.45, 0.55);
  deskGroup.add(chairSeat);
  const chairBack = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.5, 0.04), darkerWood);
  chairBack.position.set(0, 0.7, 0.76);
  deskGroup.add(chairBack);
  scene.add(deskGroup);

  // ─── Wardrobe ───
  const wardrobe = new THREE.Mesh(new THREE.BoxGeometry(0.6, 2.2, 0.5), darkWood);
  wardrobe.position.set(-w / 2 + 0.35, 1.1, d / 2 - 0.6);
  wardrobe.castShadow = true;
  scene.add(wardrobe);
  const wDoorLine = new THREE.Mesh(new THREE.BoxGeometry(0.01, 2, 0.01), darkerWood);
  wDoorLine.position.set(-w / 2 + 0.35, 1.1, d / 2 - 0.34);
  scene.add(wDoorLine);
  const wHandle = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.12, 0.03), new THREE.MeshStandardMaterial({ color: accent, metalness: 0.6, roughness: 0.3 }));
  wHandle.position.set(-w / 2 + 0.43, 1.1, d / 2 - 0.33);
  scene.add(wHandle);

  // ─── TV ───
  const tvGroup = new THREE.Group();
  tvGroup.position.set(-w / 2 + 0.06, 1.3, -d * 0.15);
  tvGroup.rotation.y = Math.PI / 2;
  const tvBody = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.7, 0.04), new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.3 }));
  tvGroup.add(tvBody);
  const tvScreen = new THREE.Mesh(new THREE.PlaneGeometry(1.15, 0.65), new THREE.MeshStandardMaterial({ color: 0x1a1a2e, emissive: 0x1a1a2e, emissiveIntensity: 0.05 }));
  tvScreen.position.z = 0.025;
  tvGroup.add(tvScreen);
  scene.add(tvGroup);

  // ─── Bathroom door ───
  const doorGroup = new THREE.Group();
  doorGroup.position.set(-w / 2 + 0.02, 0, -d / 2 + 1.2);
  doorGroup.rotation.y = Math.PI / 2;
  const door = new THREE.Mesh(new THREE.BoxGeometry(0.9, 2.2, 0.08), new THREE.MeshStandardMaterial({ color: 0xf5f0e8, roughness: 0.7 }));
  door.position.y = 1.1;
  doorGroup.add(door);
  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.04, 0.1), new THREE.MeshStandardMaterial({ color: 0xc0c0c0, metalness: 0.8, roughness: 0.2 }));
  handle.position.set(0.3, 1.0, 0.05);
  doorGroup.add(handle);
  scene.add(doorGroup);

  // ─── Suite living area ───
  if (config.id === 'suite') {
    const sofaGroup = new THREE.Group();
    sofaGroup.position.set(w / 4, 0, d / 4);
    const sofaSeat = new THREE.Mesh(new THREE.BoxGeometry(2, 0.45, 0.8), new THREE.MeshStandardMaterial({ color: 0x4a3f35, roughness: 0.8 }));
    sofaSeat.position.y = 0.225;
    sofaSeat.castShadow = true;
    sofaGroup.add(sofaSeat);
    const sofaBack = new THREE.Mesh(new THREE.BoxGeometry(2, 0.4, 0.12), new THREE.MeshStandardMaterial({ color: 0x3d3225, roughness: 0.8 }));
    sofaBack.position.set(0, 0.55, -0.34);
    sofaGroup.add(sofaBack);
    const cushion = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.35, 0.1), new THREE.MeshStandardMaterial({ color: accent, transparent: true, opacity: 0.8 }));
    cushion.position.set(-0.6, 0.6, -0.25);
    sofaGroup.add(cushion);
    const cushion2 = cushion.clone();
    cushion2.position.x = 0.6;
    sofaGroup.add(cushion2);
    const cTable = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.03, 0.5), new THREE.MeshStandardMaterial({ color: 0x5c4033, roughness: 0.4 }));
    cTable.position.set(0, 0.4, 0.7);
    sofaGroup.add(cTable);
    scene.add(sofaGroup);
  }
}

// ═══════════════════════════════════════════════════════
// THREEJS CANVAS HOOK
// ═══════════════════════════════════════════════════════

function useThreeRoom(containerRef, config, ready, aiConfig, onBitsyClick, bitsyMoodRef) {
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (!ready || !containerRef.current) return;
    const container = containerRef.current;

    // Cleanup previous instance
    if (cleanupRef.current) cleanupRef.current();

    const w = container.clientWidth;
    const h = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0e1a);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 100);
    camera.position.set(4, 3, 6);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    renderer.shadowMap.enabled = true;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const maxDist = aiConfig
      ? (aiConfig.estimated_dimensions?.width_meters || 6) * 1.5
      : config.size.w * 1.5;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 1.2, 0);
    controls.minDistance = 2;
    controls.maxDistance = maxDist;
    controls.maxPolarAngle = Math.PI * 0.85;
    controls.minPolarAngle = 0.1;
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.update();
    controlsRef.current = controls;

    // Use AI config if available, otherwise template
    if (aiConfig) {
      buildRoomFromAIConfig(scene, aiConfig);
    } else {
      buildRoom(scene, config);
    }

    // Add Bitsy avatar
    const roomW = aiConfig?.estimated_dimensions?.width_meters || config.size.w;
    const roomD = aiConfig?.estimated_dimensions?.depth_meters || config.size.d;
    const bitsyPos = { x: roomW / 2 - 0.8, y: 0, z: roomD / 2 - 0.8 };
    const bitsy = createBitsyAvatar(scene, bitsyPos);

    // Expose setMood to parent via ref
    if (bitsyMoodRef) bitsyMoodRef.current = bitsy.setMood;

    // Raycaster for Bitsy click detection
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const handlePointerDown = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(bitsy.group.children, true);
      if (hits.length > 0 && onBitsyClick) onBitsyClick();
    };
    renderer.domElement.addEventListener('pointerdown', handlePointerDown);

    // Cursor change on hover
    const handlePointerMove = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(bitsy.group.children, true);
      renderer.domElement.style.cursor = hits.length > 0 ? 'pointer' : 'grab';
    };
    renderer.domElement.addEventListener('pointermove', handlePointerMove);

    let time = 0;
    const animate = () => {
      animRef.current = requestAnimationFrame(animate);
      time += 0.016;
      controls.update();
      bitsy.animate(time);
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const ww = container.clientWidth;
      const hh = container.clientHeight;
      if (ww > 0 && hh > 0) {
        camera.aspect = ww / hh;
        camera.updateProjectionMatrix();
        renderer.setSize(ww, hh);
      }
    };
    window.addEventListener('resize', onResize);

    cleanupRef.current = () => {
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('pointerdown', handlePointerDown);
      renderer.domElement.removeEventListener('pointermove', handlePointerMove);
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (bitsyMoodRef) bitsyMoodRef.current = null;
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      cleanupRef.current = null;
    };

    return cleanupRef.current;
  }, [containerRef, config, ready, aiConfig, onBitsyClick, bitsyMoodRef]);
}

// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════

const AMENITY_ICONS = { WiFi: Wifi, AC: Wind, TV: Tv, 'Mini Bar': Coffee };

export default function MetaverseViewerPage() {
  const { hotelId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(0);
  const containerRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [groupSession, setGroupSession] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [aiConfigs, setAiConfigs] = useState([]);

  const config = ROOM_TYPES[selectedRoom];

  // Fetch AI-generated room configs
  useEffect(() => {
    fetch(`${API}/api/metaverse/room-configs/${hotelId}`)
      .then(r => r.json())
      .then(data => { if (data.success) setAiConfigs(data.configs || []); })
      .catch(() => {});
  }, [hotelId]);

  // Get AI config for current room type (match by template id mapping)
  const ROOM_TYPE_MAP = { standard: 'Standard Queen', deluxe: 'Deluxe King', suite: 'Presidential Suite' };
  const currentAiConfig = aiConfigs.find(c => c.room_type_name === ROOM_TYPE_MAP[config.id])?.config || null;

  // Pre-select room from URL param
  useEffect(() => {
    const roomParam = searchParams.get('room');
    if (roomParam) {
      const idx = ROOM_TYPES.findIndex(r => r.id === roomParam);
      if (idx >= 0) setSelectedRoom(idx);
    }
  }, [searchParams]);

  // Track view if arriving via shared link
  useEffect(() => {
    const groupId = searchParams.get('group');
    if (groupId) {
      fetch(`${API}/api/metaverse/share/${groupId}`)
        .then(r => r.json())
        .then(data => {
          if (data.success) setGroupSession(data.session);
        })
        .catch(() => {});
      fetch(`${API}/api/metaverse/share/${groupId}/view`, { method: 'POST' }).catch(() => {});
    }
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/hotels/${hotelId}/public`);
        const data = await res.json();
        setHotel(data.hotel || data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [hotelId]);

  useEffect(() => { setMounted(true); }, []);

  const hotelName = hotel?.name || 'Hotel';

  const handleShare = useCallback(async () => {
    setShareLoading(true);
    try {
      const res = await fetch(`${API}/api/metaverse/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId || 'hotel_grand_marina',
          room_type: config.id,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const shareUrl = `${window.location.origin}/metaverse/${hotelId || 'hotel_grand_marina'}?room=${config.id}&group=${data.group_id}`;
        try {
          await navigator.clipboard.writeText(shareUrl);
          setCopied(true);
          toast.success('Link copied! Share it with your travel group');
          setTimeout(() => setCopied(false), 2500);
        } catch {
          // Clipboard blocked (e.g. iframe / non-HTTPS) — show the URL as a fallback
          toast.success(shareUrl, { duration: 8000, description: 'Copy this link to share' });
        }
        setShareMenuOpen(false);
      }
    } catch {
      toast.error('Failed to create share link');
    } finally {
      setShareLoading(false);
    }
  }, [hotelId, config.id]);

  const handleBook = useCallback(() => {
    const groupId = searchParams.get('group');
    const bookUrl = groupId
      ? `/book/${hotelId || 'hotel_grand_marina'}?room=${config.id}&group=${groupId}`
      : `/book/${hotelId || 'hotel_grand_marina'}?room=${config.id}`;
    navigate(bookUrl);
    // Track booking conversion
    if (groupId) {
      fetch(`${API}/api/metaverse/share/${groupId}/book`, { method: 'POST' }).catch(() => {});
    }
  }, [hotelId, config.id, navigate, searchParams]);

  const [chatOpen, setChatOpen] = useState(false);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [voicePlaying, setVoicePlaying] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [voiceMuted, setVoiceMuted] = useState(false);
  const audioRef = useRef(null);
  const [welcomeNotif, setWelcomeNotif] = useState(null);
  const [welcomeGreeting, setWelcomeGreeting] = useState(null);
  const welcomeAudioRef = useRef(null);

  // Persistent guest ID (localStorage)
  const [guestId] = useState(() => {
    let id = localStorage.getItem('bitsy_guest_id');
    if (!id) {
      id = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem('bitsy_guest_id', id);
    }
    return id;
  });

  // Welcome-back check for returning guests
  useEffect(() => {
    if (loading) return;
    fetch(`${API}/api/metaverse/bitsy/welcome-back`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guest_id: guestId, hotel_id: hotelId, room_type: config.name }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.returning) {
          // Show floating notification
          setWelcomeNotif({
            text: data.guest_name ? `Bitsy remembers you, ${data.guest_name}!` : 'Bitsy remembers you!',
            memories: data.memories_found || 0,
          });
          setWelcomeGreeting(data.greeting_text);
          // Prepare TTS audio
          if (data.audio_base64) {
            const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
            audio.volume = 0.7;
            audio.addEventListener('play', () => setVoicePlaying(true));
            audio.addEventListener('ended', () => setVoicePlaying(false));
            audio.addEventListener('pause', () => setVoicePlaying(false));
            welcomeAudioRef.current = audio;
          }
          // Auto-open chat after 3 seconds
          setTimeout(() => {
            setChatOpen(true);
            setWelcomeNotif(null);
            // Auto-play welcome TTS
            if (welcomeAudioRef.current) {
              welcomeAudioRef.current.play().catch(() => {});
            }
          }, 3000);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hotelId]);

  // Fetch Bitsy voice greeting
  useEffect(() => {
    if (loading || voiceReady) return;
    fetch(`${API}/api/metaverse/bitsy-greeting`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hotel_id: hotelId, room_type: config.name }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.audio_base64) {
          const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          audio.volume = 0.7;
          audio.addEventListener('play', () => setVoicePlaying(true));
          audio.addEventListener('ended', () => setVoicePlaying(false));
          audio.addEventListener('pause', () => setVoicePlaying(false));
          audioRef.current = audio;
          setVoiceReady(true);
        }
      })
      .catch(() => {});
  }, [loading, hotelId, config.name, voiceReady]);

  const toggleVoice = useCallback(() => {
    if (!audioRef.current) return;
    if (voicePlaying) {
      audioRef.current.pause();
      setVoiceMuted(true);
    } else {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
      setVoiceMuted(false);
    }
  }, [voicePlaying]);

  const handleBitsyClick = useCallback(() => {
    setChatOpen(true);
    // Auto-play greeting on first Bitsy click if not already played
    if (audioRef.current && !voiceMuted && audioRef.current.currentTime === 0) {
      audioRef.current.play().catch(() => {});
    }
  }, [voiceMuted]);

  // Mood ref — bridges React state to Three.js scene
  const bitsyMoodRef = useRef(null);
  const setBitsyMood = useCallback((mood) => {
    if (bitsyMoodRef.current) bitsyMoodRef.current(mood);
  }, []);

  useThreeRoom(containerRef, config, mounted && !loading, currentAiConfig, handleBitsyClick, bitsyMoodRef);

  if (loading) {
    return (
      <div className="h-screen bg-[#0a0e1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F7931A]" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#0a0e1a] flex flex-col overflow-hidden" data-testid="metaverse-viewer">
      {/* Top Bar */}
      <div className="h-14 border-b border-white/5 bg-[#0a0e1a]/90 backdrop-blur-xl flex items-center justify-between px-4 z-20 shrink-0" data-testid="metaverse-topbar">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white" onClick={() => navigate(-1)} data-testid="metaverse-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="h-7 w-7 rounded bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
            <span className="text-white font-bold text-[10px] heading-font">B</span>
          </div>
          <div>
            <p className="text-sm font-medium text-white heading-font">{hotelName}</p>
            <p className="text-[10px] text-gray-500">BHERMES Metaverse Preview</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {groupSession && (
            <Badge variant="outline" className="border-blue-500/30 text-blue-400 bg-blue-500/5 text-[10px] gap-1" data-testid="group-badge">
              <Users className="h-3 w-3" /> Group Invite
            </Badge>
          )}
          <button
            onClick={handleShare}
            disabled={shareLoading}
            className="h-8 px-3 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors text-xs flex items-center gap-1.5"
            data-testid="share-room-btn"
          >
            {shareLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Share2 className="h-3.5 w-3.5" />}
            {copied ? 'Copied!' : 'Share Room'}
          </button>
          <button
            onClick={() => setChatOpen(true)}
            className="h-8 px-3 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors text-xs flex items-center gap-1.5"
            data-testid="chat-with-bitsy-btn"
          >
            <MessageCircle className="h-3.5 w-3.5" /> Chat with Bitsy
          </button>
          {voiceReady && (
            <button
              onClick={toggleVoice}
              className={`h-8 w-8 rounded-md border flex items-center justify-center transition-all ${
                voicePlaying
                  ? 'border-[#F7931A]/40 bg-[#F7931A]/10 text-[#F7931A]'
                  : 'border-white/10 bg-white/5 text-gray-400 hover:text-white'
              }`}
              data-testid="voice-toggle-btn"
              title={voicePlaying ? 'Mute Bitsy' : 'Play Bitsy greeting'}
            >
              {voicePlaying ? <Volume2 className="h-3.5 w-3.5 animate-pulse" /> : <VolumeX className="h-3.5 w-3.5" />}
            </button>
          )}
          <button
            onClick={() => setCheckinOpen(true)}
            className="h-8 px-3 rounded-md border border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-400 hover:text-emerald-300 transition-colors text-xs flex items-center gap-1.5"
            data-testid="virtual-checkin-btn"
          >
            <KeyRound className="h-3.5 w-3.5" /> Virtual Check-in
          </button>
          <Badge variant="outline" className="border-[#F7931A]/30 text-[#F7931A] bg-[#F7931A]/5 text-[10px] gap-1">
            <Sparkles className="h-3 w-3" /> {currentAiConfig ? 'AI Digital Twin' : 'Phase 1 MVP'}
          </Badge>
          {hotel?.trust_score && (
            <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-[10px] gap-1">
              <Star className="h-3 w-3" /> {hotel.trust_score}
            </Badge>
          )}
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex min-h-0">
        {/* 3D Container */}
        <div className="flex-1 relative" data-testid="metaverse-canvas">
          <div ref={containerRef} className="absolute inset-0" />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 text-[10px] text-gray-500 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full pointer-events-none">
            <RotateCcw className="h-3 w-3" />
            Click + drag to rotate | Scroll to zoom | Click Bitsy to chat
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-80 border-l border-white/5 bg-[#0c1020] overflow-y-auto p-4 space-y-4 shrink-0" data-testid="metaverse-panel">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 heading-font">Room Type</p>
            <div className="space-y-1.5">
              {ROOM_TYPES.map((rt, i) => (
                <button
                  key={rt.id}
                  onClick={() => setSelectedRoom(i)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all text-sm ${
                    i === selectedRoom
                      ? 'border-[#F7931A]/40 bg-[#F7931A]/5 text-white'
                      : 'border-white/5 bg-white/[0.02] text-gray-400 hover:border-white/10 hover:text-gray-300'
                  }`}
                  data-testid={`room-type-${rt.id}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium heading-font">{rt.name}</span>
                    <span className="text-xs mono">${rt.price}/night</span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5">{rt.bedType} | {rt.sqft} sqft | {rt.maxGuests} guests</p>
                </button>
              ))}
            </div>
          </div>

          <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-3" data-testid="room-details">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 heading-font">Room Details</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5 text-gray-400"><BedIcon className="h-3.5 w-3.5" /> {config.bedType}</div>
              <div className="flex items-center gap-1.5 text-gray-400"><Maximize className="h-3.5 w-3.5" /> {config.sqft} sqft</div>
              <div className="flex items-center gap-1.5 text-gray-400"><DoorOpen className="h-3.5 w-3.5" /> {config.maxGuests} guests</div>
              <div className="flex items-center gap-1.5 text-gray-400"><Bath className="h-3.5 w-3.5" /> En-suite</div>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5" data-testid="room-amenities">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 heading-font mb-2">Amenities</p>
            <div className="flex flex-wrap gap-1.5">
              {config.amenities.map(a => {
                const Icon = AMENITY_ICONS[a];
                return (
                  <Badge key={a} variant="outline" className="text-[10px] border-white/10 text-gray-400 gap-1">
                    {Icon && <Icon className="h-2.5 w-2.5" />} {a}
                  </Badge>
                );
              })}
            </div>
          </div>

          {hotel && (
            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-2" data-testid="hotel-info">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 heading-font">Hotel</p>
              <p className="text-sm text-white heading-font">{hotel.name}</p>
              <p className="text-xs text-gray-500">{hotel.location}{hotel.city ? `, ${hotel.city}` : ''}</p>
              {hotel.description && <p className="text-xs text-gray-400 leading-relaxed">{hotel.description}</p>}
            </div>
          )}

          <VoiceConfigPanel hotelId={hotelId} agent="bitsy" />

          {groupSession && (
            <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 space-y-1" data-testid="group-info">
              <div className="flex items-center gap-1.5 text-blue-400 text-xs font-medium">
                <Users className="h-3.5 w-3.5" /> Shared by {groupSession.sharer_name || 'A guest'}
              </div>
              <p className="text-[10px] text-gray-500">Book through this link for potential group discounts</p>
            </div>
          )}

          <Button
            className="w-full bg-[#F7931A] hover:bg-[#F7931A]/90 text-black font-medium"
            onClick={handleBook}
            data-testid="book-room-btn"
          >
            Book {config.name} — ${config.price}/night
          </Button>

          <div className="pt-2 text-center">
            <button
              onClick={() => navigate('/protocol')}
              className="text-[10px] text-gray-600 hover:text-[#F7931A] transition-colors inline-flex items-center gap-1"
            >
              Powered by BHERMES Protocol <ExternalLink className="h-2.5 w-2.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Bitsy Chat Overlay */}
      {chatOpen && (
        <MetaverseChat hotelId={hotelId} roomType={config.name} guestId={guestId} initialGreeting={welcomeGreeting} onMoodChange={setBitsyMood} onClose={() => setChatOpen(false)} />
      )}

      {/* Welcome-Back Notification */}
      {welcomeNotif && (
        <div
          className="absolute bottom-24 left-1/2 -translate-x-1/2 z-40 animate-in fade-in slide-in-from-bottom-4 duration-500"
          data-testid="welcome-back-notif"
        >
          <button
            onClick={() => { setChatOpen(true); setWelcomeNotif(null); if (welcomeAudioRef.current) welcomeAudioRef.current.play().catch(() => {}); }}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#F7931A]/90 to-[#B08D57]/90 backdrop-blur-xl border border-white/10 shadow-lg shadow-[#F7931A]/20 hover:shadow-[#F7931A]/40 transition-all hover:scale-105 cursor-pointer"
          >
            <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </div>
            <div className="text-left">
              <p className="text-xs font-medium text-white">{welcomeNotif.text}</p>
              <p className="text-[9px] text-white/60">Click to chat — {welcomeNotif.memories} memories recalled</p>
            </div>
          </button>
        </div>
      )}

      {/* Virtual Check-in Overlay */}
      {checkinOpen && (
        <VirtualCheckin hotelId={hotelId} onClose={() => setCheckinOpen(false)} />
      )}
    </div>
  );
}
